import PortalSkeleton from "components/PortalLoading";
import { portalLoadingLayout } from "components/PortalLoading/pageLayouts";
import { useEffect, useRef, useState } from "react";
import { FiArrowUpRight, FiChevronDown, FiGrid, FiPlus, FiSliders, FiUser } from "react-icons/fi";
import { Link, useLocation } from "react-router-dom";
import { Menu } from "primereact/menu";
import { RiExchangeDollarFill } from "react-icons/ri";
import useAuthStore from "store/authStore";
import useOverlayStore from "store/overlayStore";
import useCurrencyStore from "store/currencyStore";
import { activeCurrencies } from "utils";
import useCreateResourceModal from "./useCreateResourceModal";
import useMenuItems from "./useMenuItems";
import { activeNavigationPath } from "./navigationUtils";
import MobileMenu from "./MobileMenu";
import PortalSidebar from "./PortalSidebar";
import { Role } from "utils/server-constants/roles";
import usePortalAccessGuard from "hooks/guards/usePortalAccessGuard";
import BreadCrumbs from "components/v2/BreadCrumbs";
import NotificationBell from "components/NotificationBell";
import PageHeading from "admin/components/PageHeading";
import PortalPageSearch from "./PortalPageSearch";
import PortalHeaderTools from "./PortalHeaderTools";
import useHeaderPopupMenu from "components/Overlays/useHeaderPopupMenu";
import { PortalDateContext } from "components/PortalDatePicker";
import { portalPageMeta, isDashboardPath } from "./pageMeta";
import "./portal.css";
import "./workspace.css";
import "./dashboard.css";
import "./responsive.css";

export default function Admin2({ children, fullWidth, breadCrumbs, pageTitle, pageActions }) {
    const menu = useRef(null), createTriggerRef = useRef(null);
    const [createOpen, setCreateOpen] = useState(false);
    useHeaderPopupMenu(createOpen, menu, createTriggerRef, "portal-create-menu");
    useEffect(() => {
        document.body.classList.add("akello-portal-active");
        return () => document.body.classList.remove("akello-portal-active");
    }, []);
    const { pathname } = useLocation();
    const { hasRoles, userProfile: profile } = useAuthStore();
    const { isCheckingAccess } = usePortalAccessGuard();
    const { currency, setCurrency } = useCurrencyStore();
    const { setShowCreateRate } = useOverlayStore();
    const items = useCreateResourceModal();
    const navigation = useMenuItems();
    const activePath = activeNavigationPath(navigation, pathname);
    const group = navigation.find((item) => item.children?.some((child) => child.to === activePath));
    const current = group?.children.find((item) => item.to === activePath);
    const meta = portalPageMeta(pathname);
    const dashboard = isDashboardPath(pathname);
    const pageClass = pathname.replace(/[^a-zA-Z0-9]+/g, "-");
    const title = current?.label || ({ '/upload-books': 'Upload books', '/bulk-book-imports': 'Bulk book import', '/portal-profile': 'Profile' }[pathname]) || meta.title;
    const [compact, setCompact] = useState(() => {
        try { return localStorage.getItem('akello-portal-compact') === 'true'; } catch { return false; }
    });
    const toggleDensity = () => setCompact((value) => {
        try { localStorage.setItem('akello-portal-compact', String(!value)); } catch { /* Preference is optional. */ }
        return !value;
    });
    const initials = [profile?.first_name, profile?.last_name].filter(Boolean).map((part) => part[0]).join('').slice(0, 2).toUpperCase();

    return <PortalDateContext.Provider value={true}><div className={`portal-shell portal-page${pageClass}${compact ? ' portal-compact' : ''}`}>
        <a className="portal-skip-link" href="#portal-content">Skip to content</a>
        {isCheckingAccess && <div className="portal-access-overlay">
            <div className="portal-access-preview"><PageHeading title={meta.title} description={meta.description} />
                <PortalSkeleton {...portalLoadingLayout(pathname)} label="Checking workspace access…" />
            </div>
        </div>}
        <PortalSidebar />
        <div className="portal-workspace">
            <header className="portal-topbar">
                <div className="portal-topbar-location">
                    <div className="portal-mobile-trigger"><MobileMenu /></div>
                    <span className="portal-topbar-icon" aria-hidden="true"><FiGrid /></span>
                    <div><span className="portal-topbar-eyebrow">{group?.label || 'Akello Library'}</span><strong>{title}</strong></div>
                </div>
                <div className="portal-topbar-actions">
                    <div className="portal-notifications"><NotificationBell isAuthenticated={true} /></div>
                    <PortalHeaderTools>
                    <PortalPageSearch />
                    <Link to="/books" className="portal-library-link">Visit library <FiArrowUpRight aria-hidden="true" /></Link>
                    <button type="button" className="portal-icon-button portal-density-toggle" onClick={toggleDensity} aria-pressed={compact}
                        aria-label="Compact table spacing" title={compact ? 'Use comfortable table spacing' : 'Use compact table spacing'}><FiSliders aria-hidden="true" /></button>
                    {hasRoles([Role.SuperAdmin, Role.Admin, Role.SeniorPublisher, Role.Publisher]) && <button type="button" onClick={() => setShowCreateRate(true)} className="portal-button portal-rates-button"><RiExchangeDollarFill aria-hidden="true" /><span>Rates</span></button>}
                    {items.length > 0 && <>
                        <Menu model={items} className="portal-create-menu" popup ref={menu} id="portal-create-menu" onShow={() => setCreateOpen(true)} onHide={() => setCreateOpen(false)} />
                        <button type="button" ref={createTriggerRef} onClick={(event) => menu.current.toggle(event)} aria-controls="portal-create-menu" aria-haspopup="menu" aria-expanded={createOpen} className="portal-button portal-button-primary"><FiPlus aria-hidden="true" /><span>Create new</span><FiChevronDown aria-hidden="true" /></button>
                    </>}
                    <Link to="/my-account" className="portal-topbar-account" aria-label="Manage your account" title="Manage your account">
                        <span className="portal-avatar" aria-hidden="true">{profile?.avatar ? <img src={profile.avatar} alt="" /> : initials || <FiUser />}</span>
                        <span className="portal-topbar-account-name"><strong>{profile?.first_name || "My account"}</strong><span>My account</span></span>
                    </Link>
                    </PortalHeaderTools>
                </div>
            </header>
            {pathname === '/books/admin/all' && <div className="portal-currency-bar"><label htmlFor="portal-price-currency">Show book prices in</label><select id="portal-price-currency" value={currency} onChange={(event) => setCurrency(event.target.value)}>
                <option value={activeCurrencies.USD.code}>{activeCurrencies.USD.label}</option><option value={activeCurrencies.ZWL.code}>{activeCurrencies.ZWL.label}</option>
            </select></div>}
            {breadCrumbs && <div className="portal-breadcrumbs"><BreadCrumbs tree={breadCrumbs} /></div>}
            <main id="portal-content" tabIndex={-1} className={`portal-content${fullWidth ? ' portal-content-wide' : ''}`} aria-busy={isCheckingAccess}>
                {pageTitle && <PageHeading title={pageTitle} description={meta.description} actions={pageActions} />}
                {dashboard ? children : <div className="portal-page-body">{children}</div>}
            </main>
        </div>
    </div></PortalDateContext.Provider>;
}
